import React from 'react';
import './index.css';
import {useTranslation} from 'react-i18next';
import SEO from '../seo';
import { PAGE_SEO, COMMON_KEYWORDS } from '../seo/pageSeo';

const FactoryIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/>
        <path d="M17 18h1"/><path d="M12 18h1"/><path d="M7 18h1"/>
    </svg>
);

const MachineIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="6" width="20" height="12" rx="2"/>
        <path d="M12 12h.01"/><path d="M17 12h.01"/><path d="M7 12h.01"/>
        <path d="M2 10h20"/>
    </svg>
);

const ToolsIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
    </svg>
);

const ProductsIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
);

export default function Capacity() {
    const {t, i18n} = useTranslation();

    const getEquipList = () => {
        if (i18n.language.startsWith('vi')) {
            return ['Cắt laser', 'Máy gấp', 'Sơn phun', 'Phun cát', 'Hàn tự động', 'Thiết bị tự động hóa lớn'];
        }
        if (i18n.language.startsWith('en')) {
            return ['Laser Cutting', 'Bending Machine', 'Spray Painting', 'Shot Blasting', 'Auto Welding', 'Large-scale Production'];
        }
        return ['激光切割机', '折弯机', '喷涂机', '抛丸机', '自动焊接机', '大型自动化设备'];
    };

    const equipList = getEquipList();

    return (
        <>
        <SEO
            pageKey="capacity"
            title={PAGE_SEO.capacity.title}
            description={PAGE_SEO.capacity.description}
            keywords={COMMON_KEYWORDS}
            slug={PAGE_SEO.capacity.slug}
        />
        <div className="capacity-section">
            <div className="capacity-inner">
                {/* Header */}
                <div className="capacity-header">
                    <div className="capacity-header-tag">XIAOMAI</div>
                    <h2>{t('capacity_1')}</h2>
                    <div className="capacity-header-line" />
                </div>

                {/* KPI Row */}
                <div className="capacity-kpi-row">
                    <div className="capacity-kpi">
                        <div className="capacity-kpi-icon"><FactoryIcon /></div>
                        <div className="capacity-kpi-num">15000<span style={{fontSize: '16px'}}>m²</span></div>
                        <div className="capacity-kpi-label">{t('capacity_kpi_production_area')}</div>
                    </div>
                    <div className="capacity-kpi">
                        <div className="capacity-kpi-icon"><MachineIcon /></div>
                        <div className="capacity-kpi-num">5000+</div>
                        <div className="capacity-kpi-label">{t('capacity_kpi_units_year')}</div>
                    </div>
                    <div className="capacity-kpi">
                        <div className="capacity-kpi-icon"><ToolsIcon /></div>
                        <div className="capacity-kpi-num">6</div>
                        <div className="capacity-kpi-label">{t('capacity_kpi_major_equipment')}</div>
                    </div>
                    <div className="capacity-kpi">
                        <div className="capacity-kpi-icon"><ProductsIcon /></div>
                        <div className="capacity-kpi-num">4</div>
                        <div className="capacity-kpi-label">{t('capacity_kpi_product_series')}</div>
                    </div>
                </div>

                {/* Intro: Text + Main Image */}
                <div className="capacity-intro">
                    <div className="capacity-intro-text">
                        <h3>{t('capacity_intro_title')}</h3>
                        <p>{t('capacity_2')}</p>
                    </div>
                    <div className="capacity-intro-img-wrap">
                        <img
                            className="capacity-intro-img"
                            src="/files/images/capacity_1.jpg"
                            alt="production"
                        />
                    </div>
                </div>

                {/* Gallery: Main + Stack */}
                <div className="capacity-gallery">
                    <div className="gallery-main">
                        <img
                            className="gallery-main-img"
                            src="/files/images/capacity_2.jpg"
                            alt="factory"
                        />
                    </div>
                    <div className="gallery-stack">
                        <div className="gallery-stack-item">
                            <img className="gallery-stack-img" src="/files/images/capacity_3.jpg" alt="workshop" />
                        </div>
                        <div className="gallery-stack-item">
                            <img className="gallery-stack-img" src="/files/images/capacity_4.jpg" alt="equipment" />
                        </div>
                        <div className="gallery-stack-item">
                            <img className="gallery-stack-img" src="/files/images/capacity_5.jpg" alt="production line" />
                        </div>
                    </div>
                </div>

                {/* Equipment List */}
                <div className="capacity-equip-row">
                    {equipList.map(eq => (
                        <div key={eq} className="capacity-equip-item">
                            <div className="capacity-equip-dot" />
                            <span>{eq}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
        </>
    );
}
